import {FC, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import {errorHandler, successHandler} from '../../utils/alarmHandler';
import {formatDateTime} from '../../utils/dateFormatter';
import styles from './CreateRecordStep.module.scss';

interface FormData {
    doctor_id: number;
    start: string;
    end: string;
    owner_full_name?: string;
    phone_number?: string;
    patient_animal_type_name?: string;
    patient_breed_name?: string;
    patient_nickname?: string;
    is_online: boolean;
    is_readed: boolean;
    reason?: string;
}

type Props = {
    doctorId: number;
    start: string;
    end: string;
};

const CreateRecordStep: FC<Props> = ({doctorId, start, end}) => {
    const navigate = useNavigate();
    const [isPreload, setPreload] = useState<boolean>(false);
    const [data, setData] = useState<FormData>({
        doctor_id: doctorId,
        is_online: true,
        is_readed: false,
        start: start,
        end: end,
    });
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalData, setModalData] = useState<any>({});

    const isDisabledSendButton = () => {
        const requiredFields = [
            data.owner_full_name,
            data.patient_nickname,
            data.phone_number,
            data.patient_animal_type_name,
            data.patient_breed_name,
            data.reason
        ];
        return requiredFields.some(field => !field || field.trim() === '');
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (config.url_provet_api) {
            setPreload(true);

            axios
                .post(`${config.url_provet_api}reserve_datetime`, data, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                .then((response) => {
                    successHandler('Отлично!');
                    setModalData({
                        ...response.data.response,
                        type: 200,
                    });
                    setShowModal(true);
                })
                .catch((error) => {
                    errorHandler(error);
                    setModalData({
                        type: 400,
                        error: error.response?.data?.message || 'Произошла ошибка при записи',
                    });
                    setShowModal(true);
                })
                .finally(() => {
                    setPreload(false);
                });
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        if (modalData?.type === 200) {
            navigate('/');
        }
    };

    return (
        <section className={styles.section}>
            <div className={styles.inSection}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            Заполните данные для записи
                        </h1>
                        <p className={styles.subtitle}>
                            Заполните форму ниже чтобы записаться на прием
                        </p>
                    </div>

                    <div className={styles.appointmentInfo}>
                        <div className={styles.infoCard}>
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Дата и время:</span>
                                <span className={styles.infoValue}>{formatDateTime(start)}</span>
                            </div>
                            <div className={styles.note}>
                                <span className={styles.noteIcon}>💡</span>
                                <p className={styles.noteText}>
                                    Пожалуйста, приходите за 5-10 минут до назначенного времени с ПАСПОРТОМ ГРАЖДАНИНА
                                    РФ (тот, кто будет на приеме) и с ВЕТЕРИНАРНЫМ ПАСПОРТОМ ЖИВОТНОГО при наличии
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    ФИО владельца
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    placeholder="Иванов Иван Иванович"
                                    value={data.owner_full_name || ''}
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            owner_full_name: e.target.value !== '' ? e.target.value : undefined,
                                        });
                                    }}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Номер телефона
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="tel"
                                    className={styles.input}
                                    placeholder="+7 (900) 123-45-67"
                                    value={data.phone_number || ''}
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            phone_number: e.target.value !== '' ? e.target.value : undefined,
                                        });
                                    }}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Кличка питомца
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    placeholder="Барсик, Шарик и т.д."
                                    value={data.patient_nickname || ''}
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            patient_nickname: e.target.value !== '' ? e.target.value : undefined,
                                        });
                                    }}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Вид животного
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    placeholder="Собака, кошка, кролик и т.д."
                                    value={data.patient_animal_type_name || ''}
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            patient_animal_type_name: e.target.value !== '' ? e.target.value : undefined,
                                        });
                                    }}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>
                                    Порода
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    placeholder="Британская, овчарка и т.д."
                                    value={data.patient_breed_name || ''}
                                    onChange={(e) => {
                                        setData({
                                            ...data,
                                            patient_breed_name: e.target.value !== '' ? e.target.value : undefined,
                                        });
                                    }}
                                    required
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                Описание / причина обращения
                                <span className={styles.required}>*</span>
                            </label>
                            <textarea
                                className={styles.textarea}
                                placeholder="Опишите симптомы, причину визита"
                                rows={4}
                                value={data.reason || ''}
                                onChange={(e) => {
                                    setData({
                                        ...data,
                                        reason: e.target.value !== '' ? e.target.value : undefined,
                                    });
                                }}
                                required
                            />
                        </div>

                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={() => navigate(-1)}
                            >
                                Назад
                            </button>
                            <button
                                type="submit"
                                className={styles.submitButton}
                                disabled={isDisabledSendButton() || isPreload}
                            >
                                {isPreload ? (
                                    <>
                                        <span className={styles.spinnerSmall}></span>
                                        Отправка...
                                    </>
                                ) : (
                                    <>
                                        Записаться на прием
                                        <span className={styles.buttonIcon}>→</span>
                                    </>
                                )}
                            </button>
                        </div>

                        <div className={styles.requiredNote}>
                            <span className={styles.required}>*</span> — обязательные поля для заполнения
                        </div>
                    </form>
                </div>
            </div>

            {showModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>
                                {modalData?.type === 200 ? '✅ Запись подтверждена' : '❌ Ошибка записи'}
                            </h2>
                            <button
                                className={styles.modalClose}
                                onClick={handleCloseModal}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            {modalData?.type === 200 ? (
                                <>
                                    <div className={styles.successCard}>
                                        <div className={styles.successIcon}>🎉</div>
                                        <h3 className={styles.successTitle}>Вы успешно записались на прием!</h3>
                                        <p className={styles.successText}>
                                            Мы отправили вам подтверждение на указанный номер телефона.
                                        </p>
                                    </div>

                                    <div className={styles.appointmentDetails}>
                                        <h4 className={styles.detailsTitle}>Детали записи:</h4>
                                        <div className={styles.detailsGrid}>
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>Дата и время:</span>
                                                <span
                                                    className={styles.detailValue}>{formatDateTime(modalData.start)}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>Питомец:</span>
                                                <span className={styles.detailValue}>{modalData.patient_nickname}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>Врач:</span>
                                                <span className={styles.detailValue}>{modalData.doctor_name}</span>
                                            </div>
                                            <div className={styles.detailItem}>
                                                <span className={styles.detailLabel}>Причина:</span>
                                                <span className={styles.detailValue}>{modalData.reason}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.importantNote}>
                                        <div className={styles.noteHeader}>
                                            <span className={styles.noteIcon}>⚠️</span>
                                            <span className={styles.noteTitle}>Важная информация:</span>
                                        </div>
                                        <ul className={styles.noteList}>
                                            <li>Приходите за 5-10 минут до назначенного времени</li>
                                            <li>Возьмите с собой паспорт того, кто придет на прием</li>
                                            <li>Человек должен быть совершеннолетним</li>
                                            <li>Возьмите с собой ветеринарный паспорт питомца (если есть)</li>
                                            <li>Сообщите на ресепшн о вашем прибытии</li>
                                            <li>Рекомендуем сделать скриншот этой страницы</li>
                                        </ul>
                                    </div>

                                    <div className={styles.clinicInfo}>
                                        <p className={styles.clinicText}>
                                            Адрес клиники: г. Верещагино, ул. К. Маркса, 64
                                        </p>
                                        <p className={styles.clinicText}>
                                            Телефон для связи: 8 (902) 477-72-52
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.errorCard}>
                                    <div className={styles.errorIcon}>❌</div>
                                    <h3 className={styles.errorTitle}>Произошла ошибка</h3>
                                    <p className={styles.errorText}>{modalData.error}</p>
                                    <p className={styles.errorHelp}>
                                        Пожалуйста, попробуйте еще раз или свяжитесь с нами по телефону для записи.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                className={styles.modalButton}
                                onClick={handleCloseModal}
                            >
                                {modalData?.type === 200 ? 'Вернуться на главную' : 'Понятно'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CreateRecordStep;